import { ChartLabelSet, ChartTextKey, isChartTextKey } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** Translation key of each chart text key under `chartLabels.text`. */
export const CHART_TEXT_TRANSLATION_KEYS: Record<ChartTextKey, string> = {
  [ChartTextKey.UNKNOWN]: 'unknown',
  [ChartTextKey.UNKNOWN_SUPPLIER]: 'unknownSupplier',
  [ChartTextKey.UNKNOWN_PERIOD]: 'unknownPeriod',
  [ChartTextKey.INVOICES]: 'invoices'
};

const LABEL_SETS: readonly string[] = Object.values(ChartLabelSet);

/** Whether a value names a label set the web-app translates. */
export function isChartLabelSet(value: unknown): value is ChartLabelSet {
  return typeof value === 'string' && LABEL_SETS.includes(value);
}

/**
 * Returns a translator for chart labels: text keys always translate, values of a known label
 * set translate through it, anything else is display text and stays as is.
 */
export function useChartLabel(): (value: string, labelSet?: string) => string {
  const t = useFeatureTranslation('aiChat');

  return (value, labelSet) => {
    if (isChartTextKey(value)) return t(`chartLabels.text.${CHART_TEXT_TRANSLATION_KEYS[value]}`);
    if (!isChartLabelSet(labelSet)) return value;
    const key = `chartLabels.${labelSet}.${value}`;
    const translated = t(key);
    // A missing key means backend and frontend drifted — show the value, never the key path.
    return translated === key || translated === `features.aiChat.${key}` ? value : translated;
  };
}
